import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from '@hivebrite/design-system/Toast';

import Layout from 'hb-react/frontoffice/apps/Mentoring/components/Layout';
import { CreateMentee } from 'hb-react/frontoffice/apps/Mentoring/container/Application/Mentee/CreateMentee';
import { ReapplyMentee } from 'hb-react/frontoffice/apps/Mentoring/container/Application/Mentee/ReapplyMentee';
import { NotAuthorized } from 'hb-react/frontoffice/apps/Mentoring/container/Application/Shared/NotAuthorized';
import {
  type CustomAttributeType,
  type MenteeProfile,
  type Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { getLowerCaseMenteeLabel } from 'hb-react/frontoffice/apps/Mentoring/utils';

interface ApplyMenteeFormsProps {
  attributes: Array<CustomAttributeType>;
  menteeProfile?: MenteeProfile | { status: null };
  program: Program;
}

export const ApplyMenteeForms = ({
  program,
  menteeProfile,
  attributes,
}: ApplyMenteeFormsProps) => {
  const {
    id,
    applied_to_be_mentee_text: applicationDescription,
    mentee_label: menteeLabel,
    name,
  } = program;
  const navigate = useNavigate();

  const title = s__('Mentoring|Apply to become a %{label} in %{name}').i__({
    label: getLowerCaseMenteeLabel(menteeLabel),
    name,
  });

  const onSuccess = () => {
    navigate('/my-mentoring');
  };

  useEffect(() => {
    if (!menteeProfile?.status) {
      return;
    }

    if (['pending', 'accepted', 'on_pause'].includes(menteeProfile.status)) {
      toast(
        s__(
          'Mentoring|You have already applied to be a %{menteeLabel} of this %{programName}.',
        ).i__({
          menteeLabel: getLowerCaseMenteeLabel(menteeLabel),
          programName: name,
        }),
      );

      navigate(`/my-mentoring/${id}/card/mentor`);
    }
  }, [menteeProfile?.status, menteeLabel, name, id, navigate]);

  if (menteeProfile?.status === 'rejected_can_reapply') {
    return (
      <Layout dataTestId="can-reapply-form">
        <ReapplyMentee
          program={program}
          attributes={attributes}
          applicationDescription={applicationDescription}
          menteeProfile={
            menteeProfile as MenteeProfile & {
              status: 'rejected_can_reapply';
            }
          }
          title={title}
          onSuccess={onSuccess}
        />
      </Layout>
    );
  }

  if (menteeProfile?.status === 'rejected_cannot_reapply') {
    return (
      <NotAuthorized
        title={s__('Mentoring|Your application has been reviewed and rejected')}
        description={s__('Mentoring|You cannot reapply to this program.')}
      />
    );
  }

  if (menteeProfile?.status === 'terminated') {
    return (
      <NotAuthorized
        title={s__(
          'Mentoring|Your %{menteeLabel} profile has already been terminated',
        ).i__({
          menteeLabel: getLowerCaseMenteeLabel(menteeLabel),
        })}
        description={s__('Mentoring|You cannot reapply to this program.')}
      />
    );
  }

  if (!menteeProfile?.status) {
    return (
      <Layout dataTestId="create-form">
        <CreateMentee
          program={program}
          attributes={attributes}
          applicationDescription={applicationDescription}
          title={title}
          onSuccess={onSuccess}
        />
      </Layout>
    );
  }

  return null;
};
